import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import type { ReactNode } from 'react'
import {
  signInWithEmailAndPassword, signOut as fbSignOut, onIdTokenChanged,
} from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { auth } from './firebase'
import { api, ApiError } from './api'
import { clearCache } from './offline'
import type { UserProfile } from '../types'

interface AuthState {
  user: UserProfile | null
  firebaseUser: FirebaseUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<UserProfile>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  // Guards against a signIn() and the onIdTokenChanged listener both
  // exchanging at once on first login.
  const exchanging = useRef(false)

  const exchange = useCallback(async (fbUser: FirebaseUser): Promise<UserProfile> => {
    const idToken = await fbUser.getIdToken()
    const res = await api.post<{ ok: boolean; user: UserProfile }>(
      '/auth/exchange', { idToken },
    )
    setUser(res.user)
    return res.user
  }, [])

  /* Restore on load: if a __session cookie is still valid we trust it and skip
   * the exchange entirely. Only when it is gone do we fall back to the Firebase
   * session, which avoids a needless token round-trip on every page load. */
  useEffect(() => {
    let cancelled = false

    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      if (cancelled) return
      setFirebaseUser(fbUser)

      if (!fbUser) {
        setUser(null)
        setLoading(false)
        return
      }
      if (exchanging.current) return

      try {
        const me = await api.get<{ ok: boolean; user: UserProfile }>('/auth/me')
        if (!cancelled) setUser(me.user)
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          try {
            await exchange(fbUser)
          } catch {
            if (!cancelled) setUser(null)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })

    return () => { cancelled = true; unsub() }
  }, [exchange])

  const signIn = useCallback(async (email: string, password: string) => {
    exchanging.current = true
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password)
      return await exchange(cred.user)
    } finally {
      exchanging.current = false
      setLoading(false)
    }
  }, [exchange])

  const signOut = useCallback(async () => {
    // Clear the server session first; if that fails we still drop the client
    // session rather than leaving the UI in a half-authenticated state.
    try {
      await api.post('/auth/logout')
    } catch {
      /* ignore - local sign-out below is what the user sees */
    }
    await fbSignOut(auth)
    // Handsets get shared at mining sites, so drop the cached shell too rather
    // than leaving the previous user's app state on the device.
    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CACHES' })
    // Same reasoning for the offline read cache - it holds this person's
    // records, and the next person to sign in on this handset must not see
    // them. The write outbox is deliberately NOT cleared: unsent captures are
    // the user's work, and they still belong to whoever recorded them.
    void clearCache().catch(() => {})
    setUser(null)
  }, [])

  // Deliberately NOT Firebase's own sendPasswordResetEmail() - this app has
  // a custom branded reset flow (signed token + this app's own SMTP sender,
  // landing on /reset-password/<token>, a Jinja page) that Firebase's
  // default unbranded email would otherwise bypass entirely. See
  // anansi/app/blueprints/auth/routes.py's forgot_password() for the Jinja
  // twin of this same flow.
  const resetPassword = useCallback(async (email: string) => {
    await api.post('/auth/forgot-password', { email: email.trim() })
  }, [])

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ ok: boolean; user: UserProfile }>('/auth/me')
      setUser(me.user)
    } catch {
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, firebaseUser, loading, signIn, signOut, resetPassword, refresh }),
    [user, firebaseUser, loading, signIn, signOut, resetPassword, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
